import { useState, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Inline SVG placeholder — no external dependency, zero network request.
// Renders a light grey box with a simple image icon and "Food Image" label.
const DEFAULT_FOOD_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="250" viewBox="0 0 400 250"><rect width="400" height="250" fill="#f0f0f0"/><rect x="150" y="80" width="100" height="70" rx="6" fill="#cccccc"/><circle cx="172" cy="100" r="10" fill="#aaaaaa"/><polygon points="150,150 185,110 210,135 235,115 250,150" fill="#bbbbbb"/><text x="200" y="190" font-family="sans-serif" font-size="14" fill="#999999" text-anchor="middle">Food Image</text></svg>')}`;

export function Carousel({
  images = null,
  dots = false,
  slidesToShow = 1,
  height = '175px',
  borderRadius = '8px',
  spacing = 0,
}) {
  const [loadingStates, setLoadingStates] = useState({});

const settings = useMemo(() => ({
    dots: dots,
    infinite: true,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: false,
    adaptiveHeight: false,
}), [dots, slidesToShow]);

  const getSpacing = (spacing) => {
    if (spacing === 0) return 0;
    if (typeof spacing === 'number') return spacing * 8;
    return spacing;
  };

  const spacingPx = getSpacing(spacing);
  const halfSpacing = spacingPx / 2;

  // Use provided images or default placeholder
  let imagesToDisplay;
  
  if (!images || images.length === 0) {
    imagesToDisplay = [{
      label: "No image available",
      imgPath: DEFAULT_FOOD_IMAGE
    }];
  } else if (typeof images[0] === 'string') {
    imagesToDisplay = images.map((url, index) => ({
      label: `Image ${index + 1}`,
      imgPath: url
    }));
  } else {
    imagesToDisplay = images;
  }

  const handleImageLoad = (index) => {
    setLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleImageError = (e, index) => {
    e.target.src = DEFAULT_FOOD_IMAGE;
    setLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  return (
    <Box sx={{
      width: '100%',
      height: height,
      margin: spacing > 0 ? `0 -${halfSpacing}px` : 0,
    }}>
      <Slider {...settings}>
        {imagesToDisplay.map((step, index) => (
          <Box key={index} sx={{
            padding: spacing > 0 ? `0 ${halfSpacing}px` : 0,
            overflow: 'hidden',
            borderRadius: borderRadius,
            position: 'relative',
          }}>
            {/* Show skeleton while loading */}
            {loadingStates[index] !== false && (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={height}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  borderRadius: borderRadius,
                }}
              />
            )}
            
            <Box
              component="img"
              loading="lazy"
              sx={{
                width: '100%',
                height: height,
                objectFit: 'cover',
                display: loadingStates[index] === false ? 'block' : 'none',
                bgcolor: '#f5f5f5',
              }}
              src={step.imgPath}
              alt={step.label}
              onLoad={() => handleImageLoad(index)}
              onError={(e) => handleImageError(e, index)}
            />
          </Box>
        ))}
      </Slider>
    </Box>
  );
}